// Hub ville — ex. Chauffeur privé Lausanne (une intention = une page)
// Modèle §10 : hero + booking prérempli, déplacements locaux, aéroport,
// trajets, business, véhicules, FAQ locale, villes proches, CTA final.
import { Link } from "react-router-dom";
import { ArrowRight, Building2, Check, MapPin, Plane } from "lucide-react";
import {
  FaqBlock, JsonLd, RouteLinks, SectionTitle, SeoPage, TrustChips, VehicleCards, useHreflang, useSeo, GuideLinks,
} from "@/components/seo/Seo";
import {
  AIRPORTS, BASE_URL, BOOKING_PATH, CANTONS, CITIES, LANGS, PLACE_COORDS, S, T, p, routesFrom, routesTo,
} from "@/lib/seoContent";

const fill = (tpl, city) => tpl.replace("{city}", city);

export default function CityHub({ lang, cityId }) {
  const city = CITIES[cityId];
  const t = T[lang];
  const airport = city ? AIRPORTS[city.airport] : null;
  const canton = city && city.canton && CANTONS[city.canton] ? CANTONS[city.canton] : null;
  const path = city ? p(lang, S.chauffeur[lang], city.slug[lang]) : `/${lang}`;
  const alternates = city ? Object.fromEntries(LANGS.map((l) => [l, p(l, S.chauffeur[l], city.slug[l])])) : null;

  useSeo({ lang, title: city ? city.title[lang] : "MILES", description: city ? city.meta[lang] : "", path, type: "city", id: cityId, noindex: city?.indexable === false });
  useHreflang(alternates);

  if (!city) return null;

  const coords = PLACE_COORDS[cityId];
  const bookHref = `${BOOKING_PATH}?type=one_way&from=${encodeURIComponent(city.name[lang])}${coords ? `&from_lat=${coords[0]}&from_lon=${coords[1]}` : ""}`;

  const cityRoutes = routesFrom(cityId);
  const inboundRoutes = routesTo(cityId).filter((r) => r.origin !== city.airport);
  const airportRoutes = routesFrom(city.airport).filter((r) => r.destination !== cityId).slice(0, 6);
  const nearby = (city.nearby || []).map((id) => CITIES[id]).filter(Boolean);

  const crumbs = [
    { label: t.home, to: p(lang) },
    { label: { fr: "Chauffeur privé", en: "Chauffeur service", de: "Chauffeurservice" }[lang], to: p(lang, S.chauffeur[lang]) },
    ...(canton ? [{ label: canton.name[lang], to: p(lang, S.chauffeur[lang], canton.slug[lang]) }] : []),
    { label: city.name[lang] },
  ];

  const jsonLd = [
    { "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: crumbs.map((c, i) => ({ "@type": "ListItem", position: i + 1, name: c.label, ...(c.to ? { item: `${BASE_URL}${c.to}` } : {}) })) },
    { "@context": "https://schema.org", "@type": "Service", serviceType: "Chauffeur service",
      provider: { "@type": "Organization", name: "MILES", url: BASE_URL },
      areaServed: { "@type": "City", name: city.name.en }, name: `MILES — ${city.name.en}` },
    ...(city.faq ? [{ "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: city.faq[lang].map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })) }] : []),
  ];

  return (
    <SeoPage lang={lang} breadcrumbs={crumbs} testId={`seo-city-${cityId}`}>
      {jsonLd.map((j, i) => <JsonLd key={i} id={`city-${i}`} data={j} />)}

      <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-miles">{t.chauffeur_in} {city.name[lang]}</p>
      <h1 className="mt-3 max-w-3xl text-3xl font-bold leading-tight text-ink sm:text-5xl" data-testid="city-h1">{city.h1[lang]}</h1>
      <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate">{city.intro[lang]}</p>
      <div className="mt-7 flex flex-wrap gap-3">
        <Link to={bookHref} data-testid="city-book-cta"
          className="inline-flex h-12 items-center gap-2 rounded-xl bg-miles px-6 text-sm font-semibold text-white transition-opacity hover:opacity-90">
          {t.book_cta} <ArrowRight className="h-4 w-4" strokeWidth={2} />
        </Link>
        {airport && (
          <Link to={p(lang, S.airport[lang], airport.slug[lang])} data-testid="city-airport-cta"
            className="inline-flex h-12 items-center gap-2 rounded-xl border border-ink/15 bg-white px-6 text-sm font-semibold text-ink transition-colors hover:border-miles/40">
            <Plane className="h-4 w-4 text-miles" strokeWidth={1.5} /> {airport.name[lang]}
          </Link>
        )}
      </div>

      <ul className="mt-10 space-y-2.5">
        {city.facts[lang].map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-sm text-slate">
            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-miles" /> {f}
          </li>
        ))}
      </ul>

      <div className="mt-8"><TrustChips lang={lang} ski={["verbier", "zermatt", "davos", "st-moritz"].includes(cityId)} /></div>

      {/* Se déplacer à {ville} — zones de prise en charge réelles */}
      {city.zones && (
        <section className="mt-14" data-testid="city-zones">
          <SectionTitle title={fill(t.moving_title, city.name[lang])} />
          <div className="mt-5 grid gap-2 sm:grid-cols-2">
            {city.zones[lang].map((z) => (
              <div key={z} className="flex items-center gap-3 rounded-xl border border-ink/10 bg-white px-4 py-3.5">
                <MapPin className="h-4 w-4 shrink-0 text-miles" strokeWidth={1.5} />
                <span className="text-sm font-medium text-ink">{z}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Transfert aéroport pertinent pour la ville */}
      {airport && (
        <section className="mt-14 rounded-2xl border border-ink/10 bg-white p-6 sm:p-8" data-testid="city-airport">
          <h2 className="flex items-center gap-2 text-xl font-bold text-ink">
            <Plane className="h-5 w-5 text-miles" strokeWidth={1.5} /> {t.airport_of} — {airport.name[lang]}
          </h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate">{airport.intro[lang]}</p>
          <ul className="mt-4 space-y-2">
            {airport.facts[lang].map((f) => (
              <li key={f} className="flex items-start gap-2.5 text-sm text-slate">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" strokeWidth={2} /> {f}
              </li>
            ))}
          </ul>
          <Link to={p(lang, S.airport[lang], airport.slug[lang])} data-testid="city-airport-link"
            className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-miles">
            {airport.name[lang]} <ArrowRight className="h-4 w-4" strokeWidth={2} />
          </Link>
        </section>
      )}

      <div className="mt-14 space-y-14">
        {cityRoutes.length > 0 && (
          <RouteLinks title={`${t.popular_from} ${city.name[lang]}`} routes={cityRoutes} lang={lang} testId="city-routes" />
        )}
        {inboundRoutes.length > 0 && (
          <RouteLinks title={`${t.more_to} ${city.name[lang]}`} routes={inboundRoutes.slice(0, 6)} lang={lang} testId="city-inbound-routes" />
        )}
        {airportRoutes.length > 0 && airport && (
          <RouteLinks title={`${t.popular_from} ${airport.name[lang]}`} routes={airportRoutes} lang={lang} testId="city-airport-routes" />
        )}

        {/* MILES Business */}
        <section className="rounded-2xl border border-ink/10 bg-white p-6 sm:p-8" data-testid="city-business">
          <h2 className="flex items-center gap-2 text-xl font-bold text-ink">
            <Building2 className="h-5 w-5 text-miles" strokeWidth={1.5} /> {fill(t.business_city_title, city.name[lang])}
          </h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate">{t.business_city_sub}</p>
          <Link to={p(lang, S.business)} data-testid="city-business-link"
            className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-miles">
            {t.business_title} <ArrowRight className="h-4 w-4" strokeWidth={2} />
          </Link>
        </section>

        {/* Véhicules */}
        <section data-testid="city-vehicles">
          <SectionTitle title={t.vehicles_title} />
          <div className="mt-4"><VehicleCards lang={lang} /></div>
        </section>

        {/* FAQ locale propre à la ville */}
        <GuideLinks lang={lang} path={path} />
        {city.faq && <FaqBlock lang={lang} faq={city.faq[lang]} />}

        {/* Villes proches — maillage horizontal limité et pertinent */}
        {nearby.length > 0 && (
          <section data-testid="city-nearby">
            <h2 className="text-lg font-bold text-ink">{t.nearby_title}</h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {nearby.map((c) => (
                <Link key={c.id} to={p(lang, S.chauffeur[lang], c.slug[lang])} data-testid={`city-nearby-${c.id}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-ink/10 bg-white px-4 py-2 text-sm font-medium text-ink transition-colors hover:border-miles/40">
                  <MapPin className="h-3.5 w-3.5 text-miles" strokeWidth={1.5} /> {t.chauffeur_in} {c.name[lang]}
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

      {/* CTA final */}
      <section className="mt-14 rounded-3xl bg-miles-deep px-7 py-10 text-center sm:px-10" data-testid="city-final-cta">
        <h2 className="text-2xl font-bold text-white">{t.final_cta_title}</h2>
        <p className="mx-auto mt-2 max-w-md text-sm text-white/70">{t.final_cta_sub}</p>
        <div className="mt-6">
          <Link to={bookHref} data-testid="city-final-book-cta"
            className="inline-flex h-12 items-center gap-2 rounded-xl bg-miles px-8 text-sm font-semibold text-white transition-opacity hover:opacity-90">
            {t.book_cta} <ArrowRight className="h-4 w-4" strokeWidth={2} />
          </Link>
        </div>
      </section>
    </SeoPage>
  );
}
